import React from "react";
import styled from "styled-components";
import { Container, Section, Eyebrow } from "../components/ui/Container";
import { colors, fonts, media, radius } from "../theme";
import { Link } from "react-router-dom";

const items = [
  {
    label: "Flagship",
    title: "Everpesa Invest",
    body: "Our peer to peer lending marketplace. Invest from UGX 10,000, fund vetted small business loans, and earn typical returns of up to 7.6% per cycle.",
    href: "/invest",
    primary: true,
  },
  {
    label: "Cooperative banking",
    title: "SACCO Core Banking",
    body: "Software for cooperatives covering member management, savings, loans, and reporting in one place. Built and operated as SafariBanking.",
    href: "/products#sacco",
  },
  {
    label: "Supply chain finance",
    title: "Working Capital for Suppliers",
    body: "Invoice and purchase order financing that helps suppliers get paid faster while buyers keep their cashflow intact across the value chain.",
    href: "/products#supplychain",
  },
  {
    label: "Agriculture",
    title: "Grain Voucher System",
    body: "An aggregator across the agricultural value chain. Vouchers, traceable settlement, and digital records connecting farmers, off takers and millers.",
    href: "/products#grain",
  },
  {
    label: "Education",
    title: "Ed Tech BNPL",
    body: "A buy now pay later product for school device purchases that puts laptops and learning devices in the hands of students who need them.",
    href: "/products#bnpl",
  },
  {
    label: "Services",
    title: "Software & ICT",
    body: "The team that ships Everpesa is also for hire. Fintech engineering, payments integrations, and ICT advisory for SACCOs, MFIs and partners.",
    href: "/software",
  },
];

export const ProductsGrid = () => (
  <Section>
    <Container>
      <Head>
        <Eyebrow>What we build and operate</Eyebrow>
        <h2>One platform. Many ways to put capital to work.</h2>
        <p>
          Everpesa is built around a peer to peer marketplace, surrounded by a
          stack of products that put affordable capital and modern tools in the
          hands of African small businesses, cooperatives, farmers and students.
        </p>
      </Head>

      <Grid>
        {items.map((it) => (
          <Card key={it.title} to={it.href} $primary={it.primary}>
            <Tag $primary={it.primary}>{it.label}</Tag>
            <h3>{it.title}</h3>
            <p>{it.body}</p>
            <Learn>Learn more <span>›</span></Learn>
          </Card>
        ))}
      </Grid>
    </Container>
  </Section>
);

const Head = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: end;
  gap: 32px;
  margin-bottom: 48px;
  h2 { font-family: ${fonts.display}; max-width: 14ch; margin-top: 18px; }
  p { font-size: 17px; max-width: 480px; justify-self: end; }
  ${media.md} {
    grid-template-columns: 1fr;
    gap: 16px;
    p { justify-self: start; }
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;
  ${media.lg} { grid-template-columns: repeat(2, 1fr); }
  ${media.sm} { grid-template-columns: 1fr; }
`;

const Card = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 32px 28px;
  border-radius: ${radius.xl};
  background: ${(p) => (p.$primary ? colors.brand : "white")};
  color: ${(p) => (p.$primary ? "white" : colors.ink)};
  border: 1px solid ${(p) => (p.$primary ? "transparent" : colors.line)};
  transition: transform 240ms ease, box-shadow 240ms ease, border-color 240ms ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 24px 48px -12px rgba(10,15,31,0.15);
    border-color: ${(p) => (p.$primary ? "transparent" : colors.brand)};
  }

  h3 {
    font-family: ${fonts.display};
    color: ${(p) => (p.$primary ? "white" : colors.ink)};
    font-size: 22px;
    margin-top: 4px;
  }

  p {
    color: ${(p) => (p.$primary ? "rgba(255,255,255,0.8)" : colors.inkMuted)};
    font-size: 15px;
    line-height: 1.6;
  }
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  background: ${(p) => (p.$primary ? "rgba(255,255,255,0.18)" : colors.brandSoft)};
  color: ${(p) => (p.$primary ? "white" : colors.brand)};
`;

const Learn = styled.span`
  margin-top: auto;
  padding-top: 16px;
  font-size: 14px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: inherit;

  span { transition: transform 220ms ease; }
  ${Card}:hover & span { transform: translateX(4px); }
`;
